import _ from 'lodash';

export const DEFAULT_TABLE_PAGE_SIZE = 50;

const isEmptyValue = (value) => value === null || value === undefined || value === '';

export const distinctValues = (rows, field) =>
  [...new Set(rows.map((row) => row[field]).filter((value) => !isEmptyValue(value)))]
    .sort((a, b) => String(a).localeCompare(String(b)));

export const toFilterOptions = (values, allLabel) => [
  { key: null, value: allLabel },
  ...values.map((value) => ({ key: value, value })),
];

export const filterRows = (rows, filters) => {
  const activeFields = Object.keys(filters).filter((field) => !isEmptyValue(filters[field]));
  return rows.filter((row) => activeFields.every((field) => row[field] === filters[field]));
};

const readQueryFilters = (query, queryKeys, queryTab) => {
  // Tabs remount before router.replace settles, so filters left in the URL by another tab must be ignored.
  if (!query || (queryTab && query.tab !== queryTab)) {
    return {};
  }
  return Object.keys(queryKeys).reduce((acc, field) => {
    const value = query[queryKeys[field]];
    return isEmptyValue(value) ? acc : { ...acc, [field]: String(value) };
  }, {});
};

export default function tableControls({
  filters = {},
  queryKeys = {},
  queryTab = null,
  pageSize = DEFAULT_TABLE_PAGE_SIZE,
} = {}) {
  return {
    data() {
      return {
        tableFilters: { ...filters, ...readQueryFilters(this.$route?.query, queryKeys, queryTab) },
        tableSearchInput: '',
        tableSearch: '',
        tablePage: 1,
        tablePageSize: pageSize,
        tableRows: [],
        tablePagination: null,
        tableLoading: false,
        tableLoadSeq: 0,
      };
    },
    computed: {
      hasActiveTableFilters() {
        return Object.keys(this.tableFilters).some((field) => !isEmptyValue(this.tableFilters[field]));
      },
      tableTotalCount() {
        return this.tablePagination?.total_count || 0;
      },
      tableTotalPages() {
        return this.tablePagination?.total_pages || 0;
      },
    },
    methods: {
      setTableFilter(field, value) {
        this.tableFilters = { ...this.tableFilters, [field]: isEmptyValue(value) ? null : value };
        this.tablePage = 1;
        this.writeTableFiltersToQuery();
        return this.reloadTable();
      },
      resetTableFilters() {
        this.tableFilters = { ...filters };
        this.tablePage = 1;
      },
      submitTableSearch() {
        this.tableSearch = this.tableSearchInput.trim();
        this.tablePage = 1;
        return this.reloadTable();
      },
      clearTableSearch() {
        this.tableSearchInput = '';
        return this.tableSearch ? this.submitTableSearch() : Promise.resolve();
      },
      goToTablePage(page) {
        this.tablePage = page;
        return this.reloadTable();
      },
      reloadTable() {
        return this.loadTablePage ? this.loadTablePage() : Promise.resolve();
      },
      // Keeps the previous rows on screen until the newest request resolves; stale responses are dropped.
      async runTableLoad(request) {
        const seq = this.tableLoadSeq + 1;
        this.tableLoadSeq = seq;
        this.tableLoading = true;
        try {
          const result = await request({ page: this.tablePage, limit: this.tablePageSize });
          if (seq !== this.tableLoadSeq) {
            return;
          }
          const totalPages = result?.pagination?.total_pages || 0;
          if (!result?.items?.length && this.tablePage > 1 && this.tablePage > totalPages) {
            this.tablePage = Math.max(totalPages, 1);
            await this.reloadTable();
            return;
          }
          this.tableRows = result?.items || [];
          this.tablePagination = result?.pagination || null;
        } finally {
          if (seq === this.tableLoadSeq) {
            this.tableLoading = false;
          }
        }
      },
      writeTableFiltersToQuery() {
        const fields = Object.keys(queryKeys);
        if (!fields.length) {
          return;
        }
        const filterParams = fields.reduce(
          (acc, field) => ({ ...acc, [queryKeys[field]]: this.tableFilters[field] }),
          {}
        );
        this.$router.replace({ query: _.omitBy({ ...this.$route.query, ...filterParams }, isEmptyValue) });
      },
    },
  };
}
